import { UserCircle, LogOut, Building } from "lucide-react";
import { useAuth } from "@/providers/AuthProvider";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useState, useEffect } from "react";
import { useOrganizationSwitcher } from "@/hooks/useOrganizationSwitcher";
import { supabase } from "@/integrations/supabase/client";

interface UserProfileProps {
	collapsed: boolean;
}

interface UserProfileData {
	id: string;
	first_name?: string;
	last_name?: string;
}

export const UserProfile = ({ collapsed }: UserProfileProps) => {
	const { user, signOut, organizations, currentOrganization } = useAuth();
	const { switchOrganization, isLoading } = useOrganizationSwitcher();
	const [switchingOrgId, setSwitchingOrgId] = useState<string | null>(null);
	const [profileData, setProfileData] = useState<UserProfileData | null>(null);

	useEffect(() => {
		const fetchUserProfile = async () => {
			if (!user?.id) return;

			try {
				const { data, error } = await supabase
					.from('profiles')
					.select('id, first_name, last_name')
					.eq('id', user.id)
					.single();

				if (error) {
					console.error("Error fetching user profile:", error);
					return;
				}

				setProfileData(data);
			} catch (error) {
				console.error("Failed to fetch user profile:", error);
			}
		};

		fetchUserProfile();
	}, [user?.id]);

	if (!user) return null;

	const getInitials = () => {
		const email = user.email || "";
		return email.substring(0, 2).toUpperCase() || "U";
	};

	const getUserDisplayName = () => {
    
		if (profileData?.first_name && profileData?.last_name) {
			return `${profileData.first_name} ${profileData.last_name}`;
		}

		if (profileData?.first_name) {
			return profileData.first_name;
		}

    if (!user.email) return "User";
		return user.email.split('@')[0];
	};

	const handleOrganizationSwitch = async (orgId: string) => {
		setSwitchingOrgId(orgId);
		await switchOrganization(orgId);
		setSwitchingOrgId(null);
	};

	return (
		<div className="mt-auto p-4">
			{collapsed ? (
				<DropdownMenu>
					<DropdownMenuTrigger asChild>
						<Button variant="ghost" size="icon" className="rounded-xl h-10 w-10 bg-primary/10">
							<span className="font-medium text-sm">{getInitials()}</span>
						</Button>
					</DropdownMenuTrigger>
					<DropdownMenuContent align="end" className="w-56">
						<div className="p-2">
							<p className="font-medium">{getUserDisplayName()}</p>
							<p className="text-sm text-muted-foreground">{user.email}</p>
						</div>
						<DropdownMenuSeparator />
						
						{organizations && organizations.length > 0 && (
							<>
								<div className="px-2 py-1.5 text-sm font-medium text-muted-foreground">
									Organizations
								</div>
								{organizations.map((org) => (
									<DropdownMenuItem 
										key={org.id}
										disabled={isLoading || org.id === currentOrganization?.id}
										className={cn(
											"cursor-pointer flex items-center justify-between",
											org.id === currentOrganization?.id && "bg-primary/10 font-medium"
										)}
										onClick={() => handleOrganizationSwitch(org.id)}
									>
										<div className="flex items-center">
											<Building className="mr-2 h-4 w-4" />
											<span>{org.name}</span>
										</div>
										{switchingOrgId === org.id && (
											<div className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
										)}
									</DropdownMenuItem>
								))}
								<DropdownMenuSeparator />
							</>
						)}
						
						<DropdownMenuItem onClick={signOut} className="text-destructive cursor-pointer">
							<LogOut className="mr-2 h-4 w-4" />
							<span>Sign out</span>
						</DropdownMenuItem>
					</DropdownMenuContent>
				</DropdownMenu>
			) : (
				<div className="glass p-3 flex items-center">
					<div className="flex items-center justify-center h-10 w-10 rounded-xl bg-primary/10 text-foreground">
						<span className="font-medium text-sm">{getInitials()}</span>
					</div>
					<div className="flex-1 min-w-0 ml-3">
						<p className="font-medium truncate">{getUserDisplayName()}</p>
						<p className="text-xs text-muted-foreground truncate">{user.email}</p>
						{currentOrganization && (
							<div className="flex items-center mt-1">
								<Building className="h-3 w-3 mr-1 text-muted-foreground" />
								<DropdownMenu>
									<DropdownMenuTrigger asChild>
										<Button 
											variant="ghost" 
											className="p-0 h-auto text-xs font-normal text-muted-foreground hover:text-foreground"
										>
											{currentOrganization.name}
										</Button>
									</DropdownMenuTrigger>
									<DropdownMenuContent align="end" className="w-48">
										{organizations && organizations.map((org) => (
											<DropdownMenuItem 
												key={org.id}
												disabled={isLoading || org.id === currentOrganization?.id}
												className={cn(
													"cursor-pointer",
													org.id === currentOrganization?.id && "bg-primary/10 font-medium"
												)}
												onClick={() => handleOrganizationSwitch(org.id)}
											>
												<Building className="mr-2 h-4 w-4" />
												<span>{org.name}</span>
												{switchingOrgId === org.id && (
													<div className="ml-auto h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
												)}
											</DropdownMenuItem>
										))}
									</DropdownMenuContent>
								</DropdownMenu>
							</div>
						)}
					</div>
					<Button 
						variant="ghost" 
						size="icon" 
						className="ml-1 text-foreground hover:text-destructive"
						onClick={signOut}
					>
						<LogOut size={18} />
					</Button>
				</div>
			)}
		</div>
	);
};
